import React, { useEffect, useState } from "react";
import api from "../lib/api";
import TempleCardHeader, { CardQr } from "./TempleCardHeader";

// Half-A4 tickets: A4 portrait split into two equal halves (top / bottom); each half holds one 19.8 x 14 cm
// ticket copy (devotee / office) whose free space is the temple's advertisement area.

const adCache = new Map();
// The temple's ticket advertisement (loaded once per temple; kept out of the temple lists as it can be large).
export function useTicketAd(templeId, enabled = true) {
  const [ad, setAd] = useState(() => adCache.get(templeId) || "");
  useEffect(() => {
    if (!enabled || !templeId) return;
    if (adCache.has(templeId)) { setAd(adCache.get(templeId)); return; }
    api.get(`/temples/${templeId}/ticket-ad`).then((r) => { adCache.set(templeId, r.data.ticket_ad || ""); setAd(r.data.ticket_ad || ""); }).catch(() => {});
  }, [templeId, enabled]);
  return ad;
}

// Advertisement strip inside the ticket: fills the free space; hidden in print when no image is uploaded.
function TicketAd({ ad }) {
  return ad ? (
    <div className="flex-1 min-h-0 mx-4 mt-2 rounded border border-gold-300 overflow-hidden bg-white flex items-center justify-center" data-testid="ticket-ad">
      <img src={ad} alt="advertisement" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
    </div>
  ) : (
    <div className="ad-empty flex-1 min-h-0 mx-4 mt-2 rounded border border-dashed border-slate-300 flex items-center justify-center text-[11px] text-slate-400">
      Advertisement area · Super Admin uploads it in Masters → Temples
    </div>
  );
}

const Row = ({ label, children, testid }) => (
  <div className="flex gap-2">
    <span className="w-[2.6cm] shrink-0 text-gold-700 font-bold uppercase text-[9px] tracking-wider pt-[3px]">{label}</span>
    <span className="font-semibold text-slate-900" data-testid={testid}>{children}</span>
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <div>
    <p className="text-gold-700 font-bold uppercase text-[9px] tracking-wider">{label}</p>
    <p className={`text-[12.5px] leading-tight ${className}`}>{children}</p>
  </div>
);

const Chip = ({ children, solid }) => (
  <span className={`text-[10px] font-bold uppercase tracking-widest rounded px-2 py-[2px] ${solid ? "text-white bg-maroon-800" : "text-maroon-800 border border-maroon-800"}`}>{children}</span>
);

const Photo = ({ src, label }) => (src
  ? <img src={src} alt="" className="object-cover rounded border-2 border-gold-500 shrink-0" style={{ width: "3.6cm", height: "4.3cm" }} />
  : <div className="rounded border border-dashed border-gold-500 flex items-center justify-center text-[10px] text-slate-400 shrink-0" style={{ width: "3.6cm", height: "4.3cm" }}>{label}</div>);

export function SareeHalf({ token, temple, templeName, templeLocation, copyLabel, ad, waCard }) {
  const dmy = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "");
  return (
    <div className="print-area bg-white border-2 border-maroon-800 rounded-md overflow-hidden flex flex-col" style={{ width: "19.8cm", height: "14cm" }} data-testid="saree-half">
      <div className="flex flex-col shrink-0 bg-white" {...(waCard ? { "data-wa-card": "1" } : {})}>
        <TempleCardHeader temple={temple} templeName={templeName} location={templeLocation} title="Vastra Prasadam" />
        <div className="flex items-center justify-between gap-2 px-4 pt-3">
          <p className="font-mono font-bold text-[16px] text-maroon-900 leading-tight">{token.token_no}</p>
          <div className="flex items-center gap-1.5 shrink-0"><Chip>{token.category}</Chip>{copyLabel && <Chip solid>{copyLabel}</Chip>}</div>
        </div>
        <div className="flex gap-4 px-4 pt-3">
          <Photo src={token.photo} label="SAREE PHOTO" />
          <div className="flex-1 min-w-0 text-[12.5px] leading-[1.85]">
            {token.name && <Row label="Name">{token.name}</Row>}
            <Row label="Saree Type">{token.saree_type_name}</Row>
            <Row label="Rate"><span className="text-maroon-800">₹{token.rate}</span></Row>
            <Row label="Quantity">{token.quantity || 1}</Row>
            {token.return_type && <Row label="Return Type"><span className={token.return_type === "Returnable" ? "text-green-700" : "text-maroon-800"}>{token.return_type}</span></Row>}
            {token.booking_date && <Row label="Booking Date">{dmy(token.booking_date)}</Row>}
            {token.phone && <Row label="Phone">{token.phone}</Row>}
            <Row label="Received">{new Date(token.created_at).toLocaleString("en-IN")}</Row>
          </div>
          <div className="shrink-0 self-center"><CardQr src={token.qr_data_url} /></div>
        </div>
        <p className="px-4 pt-1.5 text-[10px] text-slate-600">Present this card / QR at the Godown for verification. Non-transferable.{templeLocation ? ` · ${templeLocation}` : ""}</p>
      </div>
      <TicketAd ad={ad} />
      <div className="mt-2 bg-maroon-800 text-gold-300 px-3 py-1 text-[9px] text-center font-semibold">Aatreya Infotech Systems</div>
    </div>
  );
}

export function ProtocolHalf({ txn, temple, templeName, copyLabel, ad, waCard }) {
  const t = txn.ticket || {};
  return (
    <div className="print-area bg-white border-2 border-maroon-800 rounded-md overflow-hidden flex flex-col" style={{ width: "19.8cm", height: "14cm" }} data-testid="protocol-half">
      <div className="flex flex-col shrink-0 bg-white" {...(waCard ? { "data-wa-card": "1" } : {})}>
        <TempleCardHeader temple={temple} templeName={templeName || txn.temple_name} title="Protocol Darshan Access Card" />
        <div className="flex items-center justify-between gap-2 px-4 pt-3">
          <div>
            <p className="text-gold-700 font-bold uppercase text-[9px] tracking-wider">Ticket No</p>
            <p className="font-mono font-bold text-[17px] text-maroon-800 leading-tight">{t.ticket_no}</p>
          </div>
          {copyLabel && <Chip solid>{copyLabel}</Chip>}
        </div>
        <div className="flex gap-4 px-4 pt-3">
          <Photo src={txn.photo} label="PHOTO" />
          <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-3 gap-y-2 content-start">
            <div className="col-span-2"><Field label="Transaction ID" className="font-mono font-bold text-[11.5px]">{txn.transaction_id}</Field></div>
            <Field label="Devotee" className="font-semibold">{txn.devotee_name}</Field>
            <Field label="Reference">{txn.reference_name || "-"}</Field>
            <Field label="Darshan" className="font-semibold text-maroon-800">{txn.darshan_type_name}</Field>
            <Field label="Devotees" className="font-semibold">{txn.num_devotees}</Field>
            <Field label="Paid / Free">{txn.paid_free}{txn.paid_free !== "FREE" ? ` · ₹${txn.amount}` : ""}</Field>
            <Field label="Booking">{txn.booking_type === "ADVANCED" ? "Advanced" : "Current"}</Field>
            <Field label="Issued">{t.issued_at ? new Date(t.issued_at).toLocaleString("en-IN") : "-"}</Field>
            <Field label={txn.darshan_date ? "Darshan Date" : "Valid Date"} className="font-semibold text-maroon-800">
              {txn.darshan_date ? txn.darshan_date.split("-").reverse().join("/") : t.issued_at ? new Date(t.issued_at).toLocaleDateString("en-IN") : "-"}</Field>
          </div>
          <div className="shrink-0 self-center"><CardQr src={t.qr_data_url} /></div>
        </div>
        <p className="px-4 pt-1.5 text-[10px] text-slate-600">Carry a valid photo ID. Non-transferable. Entry subject to verification at the temple entrance. Follow the temple dress code.</p>
      </div>
      <TicketAd ad={ad} />
      <div className="mt-2 bg-maroon-800 text-gold-100 px-3 py-1 flex items-center justify-between text-[9px]">
        <span className="font-semibold text-gold-300">|| Jai Sri Ram ||</span>
        <span>Aatreya Infotech Systems</span>
      </div>
    </div>
  );
}
